import React, { useState, useCallback } from 'react';
import { Flex, Box, Image, IconButton, Icon, Center, useBreakpointValue } from '@chakra-ui/react';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { File, FileType } from '@/lib/types';

interface CarouselProps {
  items: File[];
}

const Carousel: React.FC<CarouselProps> = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const maxHeight = useBreakpointValue({ base: '160px', md: '350px' }) || '350px';

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  }, [items.length]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  }, [items.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  if (items.length === 0) {
    return null;
  }

  return (
    <Box
      position={'relative'}
      width={'100%'}
      mx={'auto'}
      tabIndex={0}
      aria-roledescription='carousel'
      aria-label='Media carousel'
    >
      <Box
        position='relative'
        width={'100%'}
        height={maxHeight}
        overflow='hidden'
        borderRadius='md'
      >
        {items.map((item, index) => (
          <Box
            key={index}
            position='absolute'
            top={0}
            left={0}
            width='100%'
            height='100%'
            opacity={index === currentIndex ? 1 : 0}
            visibility={index === currentIndex ? 'visible' : 'hidden'}
            display={index === currentIndex ? 'block' : 'none'}
            transition='opacity 0.3s ease-in-out'
            aria-hidden={index !== currentIndex}
          >
            <Center height='100%' width='100%'>
              {item.type === FileType.Image ? (
                <Image
                  src={item.src}
                  alt={`carousel-item-${index}`}
                  maxH='100%'
                  maxW='100%'
                  objectFit='contain'
                  className='print-images'
                />
              ) : (
                <Box width='100%' height='100%'>
                  <video
                    controls
                    muted
                    loop
                    style={{ objectFit: 'contain', height: '100%', margin: '0 auto' }}
                  >
                    <source src={item.src} type='video/mp4' />
                    Your browser does not support the video tag.
                  </video>
                </Box>
              )}
            </Center>
          </Box>
        ))}
      </Box>

      {items.length > 1 && (
        <>
          <IconButton
            variant={'surface'}
            aria-label={'Previous slide'}
            size={'xs'}
            position={'absolute'}
            top={'50%'}
            left={2}
            transform={'translateY(-50%)'}
            opacity={0.7}
            _hover={{ opacity: 1 }}
            zIndex={2}
            onClick={(e) => {
              e.stopPropagation();
              prevSlide();
            }}
          >
            <Icon as={ArrowLeftIcon} w={4} h={4} />
          </IconButton>
          <IconButton
            variant={'surface'}
            aria-label={'Next slide'}
            size={'xs'}
            position={'absolute'}
            top={'50%'}
            right={2}
            transform={'translateY(-50%)'}
            opacity={0.7}
            _hover={{ opacity: 1 }}
            zIndex={2}
            onClick={(e) => {
              e.stopPropagation();
              nextSlide();
            }}
          >
            <Icon as={ArrowRightIcon} w={4} h={4} />
          </IconButton>

          <Flex justify='center' width='100%' marginTop={2} zIndex={2}>
            {items.map((_, index) => (
              <Box
                key={index}
                cursor={'pointer'}
                w={'12px'}
                h={'12px'}
                bg={index === currentIndex ? 'gray.500' : 'gray.800'}
                borderRadius={'full'}
                mx={1}
                onClick={() => goToSlide(index)}
                transition={'background-color 0.3s'}
                _hover={{ transform: 'scale(1.2)' }}
              />
            ))}
          </Flex>
        </>
      )}
    </Box>
  );
};

export default Carousel;
